/**
 * The bar that answers four questions without navigating anywhere.
 *
 * Which account, is it connected, is this real money, is this data current.
 * None of them is behind a menu, because each of them changes what an action
 * means and a person about to act should not have to go looking.
 *
 * Environment is the loudest thing here. Mistaking demo for live, or live for
 * demo, is the error a dashboard can cause with the largest consequence.
 *
 * It also carries the operating state — equity, portfolio risk, and the age of
 * each feed — because those are the figures that decide whether to act, and a
 * reader who has scrolled down a table of nineteen slots has left every panel
 * that could have told him. This strip does not scroll away.
 */
import { roleNames } from "@/shared/identity/roles";
import { Button } from "@/design-system/Button";
import { CircleAlert, Clock, LogOut, Moon, Settings, ShieldCheck, Sun } from "lucide-react";
import { NavLink } from "react-router";

import type { Theme } from "@/design-system/theme";
import { metric, metricTestId } from "@/shared/metric/catalog";

import { money } from "@/shared/money/money";
import { humanAge } from "@/shared/time/age";


import { sectionsFor } from "./navigation";
import { PlatformPulse } from "./PlatformPulse";

/**
 * NOT_OBSERVED is an account whose exchange has never been read: not an
 * outage and not an unknown, so it is not worded as either.
 */
export type Connection = "HEALTHY" | "DEGRADED" | "DISCONNECTED" | "NOT_CONNECTED" | "NOT_OBSERVED" | "UNKNOWN";
/**
 * PLATFORM is the administrator's own surface: no exchange account belongs to
 * it, so neither real nor demo money is in view and "unknown" would be a lie.
 * NONE is an account with no exchange read yet, for the same reason.
 */
export type Environment = "LIVE" | "DEMO" | "PLATFORM" | "NONE" | "UNKNOWN";

/** One feed, how old it is, and whether that age is past its budget. */
export type Source = {
  readonly name: string;
  readonly label: string;
  readonly ageSeconds: number;
  readonly stale: boolean;
};

type Props = {
  readonly platform?: boolean;
  readonly client?: boolean;
  readonly login: string;
  readonly accountId: string;
  readonly roles: readonly string[];
  readonly connection: Connection;
  readonly environment: Environment;
  /**
   * The operating state, carried here because it must survive scrolling.
   *
   * A reader who has scrolled to the bottom of a table has left every panel
   * that could tell him the account is stale or the risk has moved. The strip
   * that answers "can I trust this screen right now" cannot be one of the
   * things that scrolls away.
   *
   * Absent until the first reading arrives; the strip then carries only what
   * it knows rather than inventing placeholders.
   */
  readonly equity?: string | undefined;
  readonly risk?: string | undefined;
  /**
   * Each feed's own age, named separately.
   *
   * A live venue link does not make every figure current. The account ticks in
   * seconds and the ranking is recomputed on a quarter-hour timer, so one of
   * them is routinely minutes behind while the connection light is green. One
   * combined "data is fresh" would be true of the fast feed and false of the
   * slow one at the same moment.
   */
  readonly sources?: readonly Source[] | undefined;
  readonly theme: Theme;
  readonly onToggleTheme: () => void;
  readonly onSignOut: () => void;
  readonly signingOut: boolean;
  /** Switch between the platform and the person's own accounts, when any. */
  readonly scope?: React.ReactNode;
};

const CONNECTION_LABEL: Record<Connection, string> = {
  HEALTHY: "Bybit подключён",
  DEGRADED: "Свежая сверка недоступна",
  DISCONNECTED: "Биржа не отвечает",
  NOT_CONNECTED: "Биржевые аккаунты не подключены · торговля выключена",
  NOT_OBSERVED: "Биржа не подключена · торговля выключена",
  UNKNOWN: "Связь неизвестна",
};

const CONNECTION_DOT: Record<Connection, string> = {
  HEALTHY: "bg-status-positive",
  DEGRADED: "bg-status-warning",
  DISCONNECTED: "bg-status-negative",
  NOT_CONNECTED: "bg-text-muted",
  NOT_OBSERVED: "bg-text-muted",
  UNKNOWN: "bg-text-muted",
};

/** The same words the account panel uses. One state, one name. */
const RISK_LABEL: Record<string, string> = {
  SAFE: "Риск: в лимитах",
  ELEVATED: "Портфельный риск: повышен",
  CRITICAL: "Портфельный риск: критический",
};

const RISK_TONE: Record<string, string> = {
  SAFE: "text-risk-safe",
  ELEVATED: "text-risk-elevated",
  CRITICAL: "text-risk-critical",
};

const ENVIRONMENT_LABEL: Record<Environment, string> = {
  LIVE: "LIVE · Реальный счёт",
  DEMO: "DEMO · Учебный счёт",
  PLATFORM: "Платформа",
  NONE: "Счёт не подключён",
  UNKNOWN: "Среда неизвестна",
};

const ENVIRONMENT_STYLE: Record<Environment, string> = {
  LIVE: "border-status-negative text-status-negative",
  DEMO: "border-status-info text-status-info",
  PLATFORM: "border-border-strong text-text-secondary",
  NONE: "border-border-strong text-text-secondary",
  UNKNOWN: "border-border-strong text-text-muted",
};

/**
 * Read a value the API sent as an open string.
 *
 * A cast would be a promise that the server only ever sends these three words.
 * It is a promise this file cannot keep, and the failure would be a bar
 * reporting a state that does not exist.
 */
export function asConnection(value: string | undefined): Connection {
  return value === "HEALTHY" || value === "DEGRADED" || value === "DISCONNECTED"
    ? value
    : "UNKNOWN";
}

export function asEnvironment(value: string | undefined): Environment {
  return value === "LIVE" || value === "DEMO" ? value : "UNKNOWN";
}

export function TopBar({
  platform = false,
  client = false,
  login,
  accountId,
  roles,
  connection,
  environment,
  equity,
  risk,
  sources,
  theme,
  onToggleTheme,
  onSignOut,
  signingOut,
  scope,
}: Props): React.JSX.Element {
  const ThemeIcon = theme === "dark" ? Sun : Moon;

  return (
    // Two rows, one header. A reader sees one strip; the split exists so an
    // indicator that appears when a feed goes stale cannot push the navigation
    // onto another line, and so the two rows can be bounded separately.
    <header className="border-b border-border-subtle">
      {/* One row of a fixed height on desktop, per the contract: the header is
          identical on every page. Below the navigation breakpoint it may wrap —
          there is genuinely not room, and a horizontal scrollbar would hide
          indicators that must stay readable. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 bg-background-elevated px-4 py-2 shell:h-[52px] shell:flex-nowrap shell:py-0">
      <span className="shrink-0 font-semibold text-text-primary">Skryptos</span>

      {/* The sections sit in the bar rather than in a rail down the side. A
          fixed rail spent a seventh of a 1440 screen on nine words, and the
          table it was standing beside is the widest thing in the product. */}
      <nav aria-label="Разделы" className="hidden items-center gap-0.5 shell:flex">
        {sectionsFor(roles, platform, client).map((section) => (
          <NavLink
            key={section.path}
            to={section.path}
            end={section.path === "/app"}
            data-testid={`nav-${section.path}`}
            className={({ isActive }) =>
              [
                // Full height, so the active mark can sit on the bottom edge
                // of the header row rather than around the word. A filled tab
                // on a 52px row reads as a button that can be pressed again —
                // contract §1.
                "flex h-full min-h-8 items-center border-b-2 px-2.5 text-label-caps whitespace-nowrap transition-colors duration-150",
                "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring",
                isActive
                  ? "border-action-primary text-action-primary"
                  : "border-transparent text-text-secondary hover:text-text-primary",
                // Announced as unfinished rather than hidden: a section a
                // person cannot see is a section they assume does not exist.
                section.available ? "" : "text-text-muted",
              ].join(" ")
            }
          >
            {section.label}
            {/* The verdict on the entry as well as on the screen: one only
                visible after opening the screen is one nobody sees until they
                happen to look. */}
            {!section.available && (
              <span className="ml-1 text-helper-sm text-text-muted">скоро</span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Real or demo money is the loudest fact on an account screen. The
          platform has no money of its own in view, and its role is already
          named beside the login, so no chip stands in for one. */}
      {environment !== "PLATFORM" && environment !== "NONE" && (
        <span
          data-testid="environment"
          className={`shrink-0 rounded-md border px-2 py-0.5 text-xs font-semibold whitespace-nowrap ${ENVIRONMENT_STYLE[environment]}`}
        >
          {ENVIRONMENT_LABEL[environment]}
        </span>
      )}

      {scope}

      <span
        className="hidden min-w-0 max-w-48 shrink text-helper-sm text-text-secondary lg:block"
        data-testid="account-identity"
        title={`${login} · ${accountId} · ${roleNames(roles)}`}
      >
        {!client && <span className="block truncate text-text-primary">{accountId}</span>}
        <span className="block truncate">{login}</span>
      </span>

      {/* The operating state, between the identity and the controls. Equity
          because it is the figure the whole product is about; risk because it
          is the one that stops a cycle; freshness because the other two mean
          nothing without it. */}
      {equity !== undefined && (
        <span
          className="flex shrink-0 items-baseline gap-1.5 whitespace-nowrap text-body-main"
          data-testid={metricTestId("account.equity")}
          data-metric-id="account.equity"
          data-metric-role="global"
        >
          <span className="text-text-secondary">{metric("account.equity").label}</span>
          <span
            className="font-numeric text-body-main tabular-nums text-text-primary"
            data-testid="bar-equity"
            title={equity}
          >
            {money(equity)}
          </span>
        </span>
      )}

      <div className="ml-auto flex shrink-0 items-center gap-1">
        {/* Settings open from the icon only, never as a section (§1). */}
        <NavLink
          to="/app/settings"
          aria-label="Настройки"
          title="Настройки"
          className="inline-flex size-9 items-center justify-center rounded-row text-text-secondary transition-colors duration-150 hover:bg-surface-hover hover:text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring pointer-coarse:size-11"
        >
          <Settings aria-hidden="true" className="size-4" />
        </NavLink>
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleTheme}
          aria-label={theme === "dark" ? "Светлая тема" : "Тёмная тема"}
        >
          <ThemeIcon aria-hidden="true" className="size-4" />
        </Button>

        <Button
          variant="ghost"
          onClick={onSignOut}
          disabled={signingOut}
          // Named on the element rather than only by its text, because the
          // text is hidden on a narrow screen and the button still has to
          // announce itself.
          aria-label="Выйти"
          className="gap-2 px-3 text-sm pointer-coarse:min-h-11"
        >
          <LogOut aria-hidden="true" className="size-4" />
          <span className="hidden sm:inline">Выйти</span>
        </Button>
      </div>
      </div>

      {platform
        ? <PlatformPulse />
        // A client reads whether the exchange is connected; feed ages and the
        // portfolio-risk verdict are the operator's instruments.
        : <FreshnessBar connection={connection} risk={client ? undefined : risk} sources={client ? [] : sources ?? []} />}
    </header>
  );
}

/**
 * Whether the data can be trusted right now: the venue link, then each feed's
 * age.
 *
 * Its own row because the first one is full: an indicator that appears when a
 * feed goes stale must not push the navigation onto a second line, and the
 * contract bounds the two rows separately for that reason.
 *
 * The link moved down here from the first row once a seventh section was added
 * and the phrase started arriving as "Bybit подкл". A truncated state is a
 * state read wrong, and this row is the one that already answers the same
 * question — is what I am looking at current — with room to spare. Link health
 * and feed age are not the same fact: a feed can be minutes old while the
 * socket is alive, and the socket can be gone before any age has grown, which
 * is why both are named rather than merged into one freshness verdict.
 */
export function FreshnessBar({
  connection,
  risk,
  sources,
}: {
  readonly connection: Connection;
  readonly risk?: string | undefined;
  readonly sources: readonly Source[];
}): React.JSX.Element {
  const riskCurrent = connection === "HEALTHY" && !sources.some((source) => source.name === "account" && source.stale);
  return (
    <div className="flex min-h-7 flex-wrap items-center gap-x-4 gap-y-1 bg-background-base px-4 py-1 shell:h-7 shell:flex-nowrap shell:py-0">
      <span
        // Read as one phrase rather than as a coloured dot beside a word: the
        // colour is the redundant part, and it is the part somebody may not
        // be able to see.
        className="flex min-w-0 items-center gap-2 text-helper-sm text-text-secondary shell:shrink-0 shell:whitespace-nowrap"
        data-testid="connection-state"
      >
        <span
          aria-hidden="true"
          className={`size-2 shrink-0 rounded-full ${CONNECTION_DOT[connection]}`}
        />
        {CONNECTION_LABEL[connection]}
      </span>

      {sources.map((source) => (
        <span
          key={source.name}
          data-testid={`bar-age-${source.name}`}
          data-stale={source.stale ? "true" : "false"}
          title={`${source.label} · обновлено ${humanAge(source.ageSeconds)} назад`}
          className={`flex items-center gap-1 text-helper-sm whitespace-nowrap ${
            source.stale ? "font-medium text-status-warning" : "text-text-muted"
          }`}
        >
          {source.stale ? (
            <CircleAlert aria-hidden="true" className="size-3.5 shrink-0" />
          ) : (
            <Clock aria-hidden="true" className="size-3.5 shrink-0" />
          )}
          {source.label} {humanAge(source.ageSeconds)} назад
        </span>
      ))}

      {/* Last on the row and never truncated. Its calm wording is short and
          its critical wording is not, and the row it used to sit in had no
          room for the difference. */}
      {risk !== undefined && (
        <span
          className={`flex shrink-0 items-center gap-1.5 text-helper-sm whitespace-nowrap ${riskCurrent ? RISK_TONE[risk] ?? "text-text-muted" : "text-text-muted"}`}
          data-testid="bar-risk"
        >
          <ShieldCheck aria-hidden="true" className="size-3.5 shrink-0" />
          {riskCurrent ? RISK_LABEL[risk] ?? risk : `Последнее известное · ${RISK_LABEL[risk] ?? risk}`}
        </span>
      )}
    </div>
  );
}
